import { AssignmentSchema } from '../helper';

/**
 * Setting definition ids for the Windows LAPS configuration policy.
 *
 * These are the Endpoint Security (Account Protection) CSP settings
 * (`device_vendor_msft_laps_policies_*`) that replace the legacy
 * Administrative Templates (ADMX) `admx_admpwd` settings catalog family.
 */
export const LAPS_SETTING_IDS = {
    /** Master switch: where the local admin password is backed up (Disabled / Entra ID / Active Directory). */
    backupDirectory: 'device_vendor_msft_laps_policies_backupdirectory',
    /** Max password age — child of backupDirectory when backing up to Microsoft Entra ID (value `_1`). */
    passwordAgeDaysAad: 'device_vendor_msft_laps_policies_passwordagedays_aad',
    /**
     * Max password age — child of backupDirectory when backing up to Active Directory (value `_2`).
     * NOTE: the AD branch uses the un-suffixed id (NOT `_ad`); Graph rejects `passwordagedays_ad`.
     */
    passwordAgeDaysAd: 'device_vendor_msft_laps_policies_passwordagedays',
    passwordComplexity: 'device_vendor_msft_laps_policies_passwordcomplexity',
    passwordLength: 'device_vendor_msft_laps_policies_passwordlength',
    administratorAccountName: 'device_vendor_msft_laps_policies_administratoraccountname',
    postAuthenticationActions: 'device_vendor_msft_laps_policies_postauthenticationactions',
    postAuthenticationResetDelay: 'device_vendor_msft_laps_policies_postauthenticationresetdelay',
    automaticAccountManagementEnabled: 'device_vendor_msft_laps_policies_automaticaccountmanagementenabled',
    // Children of automaticAccountManagementEnabled (only valid when it is `_true`).
    automaticAccountManagementTarget: 'device_vendor_msft_laps_policies_automaticaccountmanagementtarget',
    automaticAccountManagementRandomizeName: 'device_vendor_msft_laps_policies_automaticaccountmanagementrandomizename',
    automaticAccountManagementEnableAccount: 'device_vendor_msft_laps_policies_automaticaccountmanagementenableaccount',
    automaticAccountManagementNameOrPrefix: 'device_vendor_msft_laps_policies_automaticaccountmanagementnameorprefix',
    // ── Active-Directory-only children (only valid when backupDirectory === 2 / hybrid-joined) ──
    // Verified against the Graph "Available child settings" list for backupdirectory_2.
    passwordExpirationProtectionEnabled: 'device_vendor_msft_laps_policies_passwordexpirationprotectionenabled',
    adPasswordEncryptionEnabled: 'device_vendor_msft_laps_policies_adpasswordencryptionenabled',
    adPasswordEncryptionPrincipal: 'device_vendor_msft_laps_policies_adpasswordencryptionprincipal',
    adEncryptedPasswordHistorySize: 'device_vendor_msft_laps_policies_adencryptedpasswordhistorysize',
} as const;

export const CHOICE_SETTING_INSTANCE_ODATA_TYPE = '#microsoft.graph.deviceManagementConfigurationChoiceSettingInstance';
export const SIMPLE_SETTING_INSTANCE_ODATA_TYPE = '#microsoft.graph.deviceManagementConfigurationSimpleSettingInstance';
export const INTEGER_SETTING_VALUE_ODATA_TYPE = '#microsoft.graph.deviceManagementConfigurationIntegerSettingValue';
export const STRING_SETTING_VALUE_ODATA_TYPE = '#microsoft.graph.deviceManagementConfigurationStringSettingValue';

/**
 * Policy-level template reference that binds the policy to the Endpoint Security
 * "Account Protection — Local admin password solution (Windows LAPS)" template.
 */
export const LAPS_POLICY_TEMPLATE_REFERENCE = {
    templateId: 'adc46e5a-f4aa-4ff6-aeff-4f27bc525796_1',
} as const;

/**
 * Per-setting template references used when emitting the Endpoint Security policy.
 *
 * The `settingValueTemplateId` is a fixed per-setting slot (it does NOT change when a
 * different choice option is selected), so the same value reference is reused for every
 * option of a given setting. Child settings (e.g. `passwordagedays_aad`) carry no
 * template reference and are intentionally omitted here.
 */
export const LAPS_TEMPLATE_REFS: Record<string, { instance: string; value: string }> = {
    [LAPS_SETTING_IDS.backupDirectory]: {
        instance: 'a3270f64-e493-499d-8900-90290f61ed8a',
        value: '4d90f03d-e14c-43c4-86da-681da96a2f92',
    },
    [LAPS_SETTING_IDS.administratorAccountName]: {
        instance: 'd3d7d492-0019-4f56-96f8-1967f7deabeb',
        value: '992c7fce-f9e4-46ab-ac11-e167398859ea',
    },
    [LAPS_SETTING_IDS.passwordComplexity]: {
        instance: '8a7459e8-1d1c-458a-8906-7b27d216de52',
        value: 'aa883ab5-625e-4e3b-b830-a37a4bb8ce01',
    },
    [LAPS_SETTING_IDS.passwordLength]: {
        instance: 'da7a1dbd-caf7-4341-ab63-ece6f994ff02',
        value: 'd08f1266-5345-4f53-8ae1-4c20e6cb5ec9',
    },
    [LAPS_SETTING_IDS.postAuthenticationActions]: {
        instance: 'd9282eb1-d187-42ae-b366-7081f32dcfff',
        value: '68ff4f78-baa8-4b32-bf3d-5ad5566d8142',
    },
    [LAPS_SETTING_IDS.postAuthenticationResetDelay]: {
        instance: 'a9e21166-4055-4042-9372-efaf3ef41868',
        value: '0deb6aee-8dac-40c4-a9dd-c3718e5c1d52',
    },
    // Verified against a real Account Protection (template adc46e5a..._1) policy export.
    [LAPS_SETTING_IDS.automaticAccountManagementEnabled]: {
        instance: '0d70f6fb-8f35-4fba-8ada-494b8a478703',
        value: '5db30960-1e02-4100-9e2c-a2212596b054',
    },
};

/**
 * Value-only template references for child settings that carry a `settingValueTemplateReference`
 * (but no `settingInstanceTemplateReference`). The automatic-account-management children all reuse the
 * parent setting's value-template id, per the verified Account Protection policy export.
 */
export const LAPS_CHILD_VALUE_TEMPLATE_REFS: Record<string, string> = {
    [LAPS_SETTING_IDS.automaticAccountManagementTarget]: '5db30960-1e02-4100-9e2c-a2212596b054',
    [LAPS_SETTING_IDS.automaticAccountManagementRandomizeName]: '5db30960-1e02-4100-9e2c-a2212596b054',
    [LAPS_SETTING_IDS.automaticAccountManagementEnableAccount]: '5db30960-1e02-4100-9e2c-a2212596b054',
    [LAPS_SETTING_IDS.automaticAccountManagementNameOrPrefix]: '5db30960-1e02-4100-9e2c-a2212596b054',
};

/** Which account automatic account management manages. */
export const LAPS_AUTO_ACCOUNT_TARGET_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Manage the built-in administrator account' },
    { value: 1, label: 'Manage a new custom administrator account (Default)' },
];

/**
 * Options for the automatic-account Randomize Name setting. Used by BOTH the desired dropdown and the
 * current-value label so the two columns render identical text — keep this the single source of truth.
 * `notConfigured` = omitted from the policy.
 */
export const LAPS_AUTO_ACCOUNT_RANDOMIZE_NAME_OPTIONS: {
    value: 'notConfigured' | 'enabled' | 'disabled';
    label: string;
}[] = [
    { value: 'notConfigured', label: 'Not configured' },
    { value: 'disabled', label: 'The name of the target account will not use a random numeric suffix. (Default)' },
    { value: 'enabled', label: 'The name of the target account will use a random numeric suffix.' },
];

/**
 * Options for the automatic-account Enable Account setting. Used by BOTH the desired dropdown and the
 * current-value label so the two columns render identical text — keep this the single source of truth.
 * `notConfigured` = omitted from the policy.
 */
export const LAPS_AUTO_ACCOUNT_ENABLE_ACCOUNT_OPTIONS: {
    value: 'notConfigured' | 'enabled' | 'disabled';
    label: string;
}[] = [
    { value: 'notConfigured', label: 'Not configured' },
    { value: 'disabled', label: 'The target account will be disabled (Default)' },
    { value: 'enabled', label: 'The target account will be enabled' },
];

/**
 * Descriptive enabled/disabled options shared by the desired dropdown AND the current-value label, so
 * the two columns render the exact same text. Keep these the single source of truth for the labels.
 */
export const LAPS_AD_PASSWORD_ENCRYPTION_OPTIONS: { value: 'enabled' | 'disabled'; label: string }[] = [
    { value: 'enabled', label: 'Store the password in encrypted form in Active Directory (Default)' },
    { value: 'disabled', label: 'Store the password in clear-text in Active Directory' },
];

export const LAPS_PASSWORD_EXPIRATION_PROTECTION_OPTIONS: { value: 'enabled' | 'disabled'; label: string }[] = [
    { value: 'enabled', label: 'Allow Configured password expiration timestamp to exceed maximum password age' },
    {
        value: 'disabled',
        label: 'Do not allow Configured password expiration timestamp to exceed maximum password age (Default)',
    },
];

/** Backup directory targets for the local administrator password. */
export const LAPS_BACKUP_DIRECTORY_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Disabled (password will not be backed up) (Default)' },
    { value: 1, label: 'Backup the password to Microsoft Entra ID only' },
    { value: 2, label: 'Backup the password to Active Directory only' },
];

/** Password complexity levels supported by Windows LAPS. */
export const LAPS_COMPLEXITY_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: 'Large letters' },
    { value: 2, label: 'Large letters + small letters' },
    { value: 3, label: 'Large letters + small letters + numbers' },
    { value: 4, label: 'Large letters + small letters + numbers + special characters (default)' },
    { value: 5, label: 'Large letters + small letters + numbers + special characters (improved readability)' },
    { value: 6, label: 'Passphrase (long words)' },
    { value: 7, label: 'Passphrase (short words)' },
    { value: 8, label: 'Passphrase (short words with unique prefixes)' },
];

/**
 * Actions taken on expiry of the post-authentication grace period. `'notConfigured'` = omitted from the
 * policy. A non-null sentinel is used (not `null`) so Angular Material's mat-select can actually select and
 * display the "Not configured" option — a `null` option value is treated by mat-select as a "clear".
 */
export const LAPS_POST_AUTH_ACTION_OPTIONS: { value: number | 'notConfigured'; label: string }[] = [
    { value: 'notConfigured', label: 'Not configured' },
    { value: 1, label: 'Reset password: upon expiry of the grace period, the managed account password will be reset.' },
    {
        value: 3,
        label: 'Reset the password and logoff the managed account: upon expiry of the grace period, the managed account password will be reset and any interactive logon sessions using the managed account will terminated. (Default).',
    },
    {
        value: 5,
        label: 'Reset the password and reboot: upon expiry of the grace period, the managed account password will be reset and the managed device will be immediately rebooted.',
    },
    {
        value: 11,
        label: 'Reset the password, logoff the managed account, and terminate any remaining processes: upon expiration of the grace period, the managed account password is reset, any interactive logon sessions using the managed account are logged off, and any remaining processes are terminated.',
    },
];

/** Tri-state for settings that can be left unmanaged. `notConfigured` = omitted from the policy. */
export const LAPS_AUTOMATIC_ACCOUNT_OPTIONS: { value: 'notConfigured' | 'enabled' | 'disabled'; label: string }[] = [
    { value: 'notConfigured', label: 'Not configured' },
    { value: 'disabled', label: 'The target account will not be automatically managed (Default)' },
    { value: 'enabled', label: 'The target account will be automatically managed' },
];

/** The editable form value backing the Windows LAPS baseline component. */
export interface LapsConfigurationPolicyForm {
    policy: {
        name: string;
        description: string;
        platform: 'windows10';
        technologies: 'mdm';
    };
    settings: LapsSettingsForm;
    assignments: {
        allUsers: boolean;
        allDevices: boolean;
        includedGroups: string[];
        groupInclusion: boolean;
    };
}

export interface LapsSettingsForm {
    /** 0 = Disabled, 1 = Microsoft Entra ID, 2 = Active Directory. */
    backupDirectory: number;
    /** When false, Password Age is left "not configured" (omitted from the policy). */
    configurePasswordAge: boolean;
    passwordAgeDays: number;
    /** When false, Password Length is left "not configured" (omitted from the policy). */
    configurePasswordLength: boolean;
    passwordLength: number;
    passwordComplexity: number;
    administratorAccountName: string;
    /** 'notConfigured' = omitted from the policy. */
    postAuthenticationActions: number | 'notConfigured';
    /** When false, Post-Authentication Reset Delay is left "not configured" (omitted from the policy). */
    configurePostAuthenticationResetDelay: boolean;
    postAuthenticationResetDelay: number;
    /** 'notConfigured' = omitted from the policy. */
    automaticAccountManagementEnabled: 'notConfigured' | 'enabled' | 'disabled';
    // Children of automaticAccountManagementEnabled (only emitted when it is 'enabled').
    /** 'notConfigured' = omitted from the policy. */
    automaticAccountManagementTarget: number | 'notConfigured';
    /** 'notConfigured' = omitted from the policy. */
    automaticAccountManagementRandomizeName: 'notConfigured' | 'enabled' | 'disabled';
    /** 'notConfigured' = omitted from the policy. */
    automaticAccountManagementEnableAccount: 'notConfigured' | 'enabled' | 'disabled';
    automaticAccountManagementNameOrPrefix: string;
    // Active-Directory-only (backupDirectory === 2)
    passwordExpirationProtectionEnabled: 'enabled' | 'disabled';
    adPasswordEncryptionEnabled: 'enabled' | 'disabled';
    adPasswordEncryptionPrincipal: string;
    /** When false, AD Encrypted Password History Size is left "not configured" (omitted from the policy). */
    configureAdEncryptedPasswordHistorySize: boolean;
    adEncryptedPasswordHistorySize: number;
}

/**
 * JSON-schema shape describing the desired Windows LAPS configuration policy.
 * The settings are intentionally typed loosely (the nested children are read/written via helpers).
 */
export interface DeviceConfigurationWindowsLAPSSchema {
    type: 'array';
    contains: {
        type: 'object';
        required: ['assignments', 'settings'];
        properties: {
            name: { type: 'string'; const: string };
            description: { type: 'string'; const: string };
            platform: { type: 'string'; const: 'windows10' };
            technologies: { type: 'string'; const: 'mdm' };
            assignments: AssignmentSchema;
            settings: LapsSettingsSchema;
        };
    };
}

export interface LapsSettingsSchema {
    type: 'array';
    minItems: number;
    maxItems: number;
    items: {
        type: 'object';
        properties: {
            settingInstance: {
                type: 'object';
                oneOf: LapsSettingInstanceSchema[];
            };
        };
    };
}

export interface LapsSettingInstanceSchema {
    properties: {
        // Optional because "not configured" nodes omit it (they describe no real Graph setting).
        '@odata.type'?: { type: 'string'; const: string };
        /**
         * `type: 'null'` marks the setting as "not configured": the entry stays in the schema (identity kept
         * via `const`) but matches no real Graph setting, so the setting is asserted absent from the policy.
         * `type: 'string'` is a normal configured setting. Absence is enforced by the array minItems/maxItems,
         * which count only configured settings; remediation and the form reader skip `type: 'null'` nodes.
         */
        settingDefinitionId: { type: 'string' | 'null'; const: string };
        choiceSettingValue?: {
            type: 'object';
            properties: {
                value: { type: 'string'; const: string };
                children?: {
                    type: 'array';
                    minItems: number;
                    maxItems: number;
                    items: {
                        oneOf: LapsSettingInstanceSchema[];
                    };
                };
            };
        };
        simpleSettingValue?: {
            type: 'object';
            properties: {
                '@odata.type': { type: 'string'; const: string };
                value: { type: 'number' | 'string'; const: number | string };
            };
        };
    };
}

export type LapsChildSettingSchema = LapsSettingInstanceSchema;
